import React, { useEffect, useState, useContext } from 'react'
import { object } from 'prop-types'
import { navigate } from 'gatsby'

import styled from 'styled-components'

import { setPhotoSrcs, setTotalPrice, selectProductVariant, handleSkinsQuantity } from 'utils/buyFlow'

import * as spacing from 'styles/spacing'
import { EngravingContext } from 'components/buy/Engraving/EngravingContext'
import Chrome from 'components/buy/BuyChrome'
import BuyOverview from 'components/buy/BuyOverview'
import BuySection from 'components/buy/BuySection'
import BuyDevices from 'components/buy/BuyDevices'
import BuyCartridges from 'components/buy/BuyCartridges'
import BuyAccessories from 'components/buy/BuyAccessories'
import BuySkins from 'components/buy/BuySkins'
import Addon from 'components/buy/Addon'
import Choice from 'components/buy/Choice'
import BuySummary from 'components/buy/BuySummary'
import Engraving from 'components/buy/Engraving'
import MobileOption from 'components/buy/MobileOption'
import Breadcrumbs from 'components/buy/Breadcrumbs'
import RelatedProducts from 'components/buy/RelatedProducts'
import FrequencyBuySection from 'components/buy/FrequencyBuySection'
import { borderColor } from 'styles/colors'
import { H3 } from 'styles/typography'
import NativeAuthContext from 'components/NativeAuth'

const Heading = styled(H3)`
  border-bottom: 1px solid ${borderColor};
  text-align: center;
  padding: ${spacing.medium} ${spacing.large};
  margin-bottom: 0;
`

const MobileWrap = styled.div`
  margin: -${spacing.large};
`

const BuyTemplate = ({ pageContext }) => {
  const { setVisible, visible, currentView, engraving, resetEngraving, engravingProgress, setProgress } = useContext(
    EngravingContext
  )

  const { province } = useContext(NativeAuthContext)

  const { collectionTitle } = pageContext
  const [collection, setCollection] = useState(pageContext.collection)
  const [price, setPrice] = useState(setTotalPrice(collection))
  const [cartridgesVisible, setCartridgesVisible] = useState(true)
  const [skinsModalVisible, setSkinsModaVisible] = useState(false)

  useEffect(() => {
    return () => {
      resetEngraving()
    }
  }, [])

  useEffect(() => {
    setPrice(setTotalPrice(collection))
  }, [collection])

  useEffect(() => {
    if (collectionTitle.includes('epen') && province === 'BC') {
      navigate('/')
    }
  }, [province, collectionTitle])

  const selectedVariant = collection.devices
    .find(p => p.variants.find(v => v.quantity > 0))
    .variants.find(v => v.quantity > 0)

  let selectedProduct = collection.devices.find(p => p.variants.find(v => v.id === selectedVariant.id))

  const isStarterKit = collectionTitle === 'epod-starter-kit'

  const isEpod =
    isStarterKit ||
    collectionTitle === 'engravable' ||
    collectionTitle === 'epod-devices' ||
    collectionTitle === 'limited-edition-epod' ||
    collectionTitle === 'frequency-epod-devices'

  const isDevice = isEpod || collectionTitle === 'epen-3'

  const photoSrcs = !visible ? [selectedVariant.image] : setPhotoSrcs(selectedVariant, currentView)
  const backgroundSrc = isDevice ? setPhotoSrcs(selectedVariant, null, true) : null

  const isEngravable =
    (collectionTitle === 'epod' || collectionTitle === 'engravable') && selectedProduct.tags.includes('engravable')

  const handleDeviceSelect = variantId => {
    const devices = selectProductVariant({
      products: collection.devices,
      variantId
    })

    setCollection({
      ...collection,
      devices
    })
  }

  const handleAddCartridge = (product, variants) => {
    const cartridgeIndex = collection.cartridges.findIndex(c => c.id === product.id)
    collection.cartridges[cartridgeIndex].variants = variants
    setCollection(collection)
    setPrice(setTotalPrice(collection))
  }

  const handleCartridgeRemove = cartridgeId => {
    const upToDateCollection = { ...collection }
    const cartridgeIndex = upToDateCollection.cartridges.findIndex(c => c.id === cartridgeId)
    upToDateCollection.cartridges[cartridgeIndex].variants.forEach(v => {
      v.quantity = 0
    })
    setCollection(upToDateCollection)
    setPrice(setTotalPrice(upToDateCollection))
  }

  const handleAccessoryQuantityChange = (value, productTitle, productId) => {
    const upToDateCollection = { ...collection }
    const accessoryIndex = collection.accessories.findIndex(a => a.id === productId)
    upToDateCollection.accessories[accessoryIndex].variants[0].quantity += value
    setCollection(upToDateCollection)
    setPrice(setTotalPrice(upToDateCollection))
  }

  const handleSkinRemove = skinId => {
    const upToDateCollection = { ...collection }
    const skinIndex = collection.skins.findIndex(skin => skin.id === skinId)
    upToDateCollection.skins[skinIndex].variants[0].quantity = 0
    setCollection(upToDateCollection)
    setPrice(setTotalPrice(upToDateCollection))
  }

  const handleAddSkins = (quantity, productId, variants) => {
    handleSkinsQuantity(collection, quantity, productId, variants)
  }

  return (
    <>
      <Chrome
        photos={photoSrcs}
        backgroundSrc={backgroundSrc}
        summary={<BuySummary totalPrice={price} selectedProduct={selectedProduct} collection={collection} />}
        breadcrumbs={
          visible && (
            <Breadcrumbs
              progress={engravingProgress}
              items={[
                {
                  label: 'Front',
                  onClick: () => {
                    setProgress('front')
                  },
                  completed: true
                },
                {
                  label: 'Back',
                  onClick: () => {
                    setProgress('back')
                  },
                  completed: currentView.item === 'back' || currentView.item === 'box' || currentView.item === 'summary'
                },
                {
                  label: 'Message',
                  onClick: () => {
                    setProgress('box')
                  },
                  completed: currentView.item === 'box' || currentView.item === 'summary'
                },
                {
                  label: 'Summary',
                  completed: currentView.item === 'summary'
                }
              ]}
            />
          )
        }
        fullHeightSidebar={collectionTitle === 'epen-3'}
        relatedProducts={
          !visible && collection.relatedProducts && <RelatedProducts products={collection.relatedProducts} />
        }
      >
        {isEngravable && visible ? (
          <>
            <Engraving />
          </>
        ) : (
          <>
            <MobileWrap className="u-hide-medium-up">
              <Heading>{collection.title}</Heading>
              {/* Select color */}
              <MobileOption heading={collection.boxContent ? "Features & What's in the box" : 'Features'}>
                <BuyOverview
                  title={collection.title}
                  description={collection.description}
                  boxContent={collection.boxContent}
                  meta={
                    collectionTitle === 'frequency-epod-devices' && (
                      <p>
                        Explore our{' '}
                        <a href="https://ca-frequency.vuse.com/" target="_blank" rel="noreferrer">
                          Interactive Frequency Experience
                        </a>
                      </p>
                    )
                  }
                />
              </MobileOption>

              {collectionTitle !== 'frequency-epod-devices' ? (
                <MobileOption
                  heading={collectionTitle === 'epod-starter-kit' ? 'Choose your kit' : 'Choose your colour'}
                >
                  <BuyDevices
                    devices={collection.devices}
                    collectionTitle={collectionTitle}
                    handleDeviceSelect={handleDeviceSelect}
                  />
                </MobileOption>
              ) : (
                <FrequencyBuySection
                  devices={collection.devices}
                  collectionTitle={collectionTitle}
                  handleDeviceSelect={handleDeviceSelect}
                />
              )}

              {/* Engraving */}
              {isEngravable && (
                <MobileOption
                  heading="Engrave your device"
                  onAdd={() => {
                    setVisible(true)
                  }}
                  isEngraving
                />
              )}

              {/* Add cartridges */}
              {collection.cartridges.length > 0 && !collection.hideCartridges && (
                <MobileOption
                  heading="Add cartridges"
                  products={collection.cartridges.filter(p => p.variants.find(v => v.quantity > 0))}
                  productType="cartridges"
                >
                  <BuyCartridges
                    cartridges={collection.cartridges}
                    handleAddCartridge={handleAddCartridge}
                    handleCartridgeRemove={handleCartridgeRemove}
                  />
                </MobileOption>
              )}

              {/* Skins */}
              {collection.skins.length > 0 && !collection.hideSkins && (
                <>
                  <MobileOption
                    heading="Add skin"
                    onAdd={() => {
                      setSkinsModaVisible(true)
                    }}
                    products={collection.skins.filter(p => p.variants.find(v => v.quantity > 0))}
                    productType="skins"
                  />
                </>
              )}

              {/* Accessories (only shown if ePod) */}
              {isEpod && !collection.hideAccessories && (
                <MobileOption
                  heading="Add accessories"
                  products={collection.accessories.filter(p => p.variants.find(v => v.quantity > 0))}
                  productType="accessories"
                >
                  <BuyAccessories
                    accessories={collection.accessories}
                    handleAccessoryQuantityChange={handleAccessoryQuantityChange}
                  />
                </MobileOption>
              )}
            </MobileWrap>

            <div className="u-hide-medium-down">
              <BuyOverview
                title={collection.title}
                description={collection.description}
                boxContent={collection.boxContent}
                meta={
                  collectionTitle === 'frequency-epod-devices' && (
                    <p>
                      Explore our{' '}
                      <a href="https://ca-frequency.vuse.com/" target="_blank" rel="noreferrer">
                        Interactive Frequency Experience
                      </a>
                    </p>
                  )
                }
              />

              {collectionTitle !== 'frequency-epod-devices' ? (
                <BuySection
                  id="colour"
                  heading={collectionTitle === 'epod-starter-kit' ? 'Choose your kit' : 'Choose your colour'}
                >
                  <BuyDevices
                    devices={collection.devices}
                    collectionTitle={collectionTitle}
                    handleDeviceSelect={handleDeviceSelect}
                  />
                </BuySection>
              ) : (
                <FrequencyBuySection
                  devices={collection.devices}
                  collectionTitle={collectionTitle}
                  handleDeviceSelect={handleDeviceSelect}
                />
              )}

              {isEngravable && (
                <BuySection id="engraving" heading="Add some more personalization by engraving your device">
                  <Addon
                    figure={`${process.env.BASE_PATH}/images/engraving/thumbnail.png`}
                    heading="Engraved device"
                    description="Add a pattern, a mini icon or a text to your device"
                    onAdd={() => {
                      setVisible(true)
                    }}
                    isEngraving
                    active={engraving.proceeded}
                    isAvailableForSale={selectedVariant.availableForSale}
                  />
                </BuySection>
              )}

              {collection.cartridges.length > 0 && !collection.hideCartridges && (
                <BuySection id="cartridges" heading="Do you want to add cartridges to your order?" price="$13.00">
                  <Choice
                    active={cartridgesVisible}
                    label="Yes please"
                    onClick={() => {
                      setCartridgesVisible(true)
                    }}
                  />
                  <Choice
                    active={!cartridgesVisible}
                    label="I'm fine"
                    onClick={() => {
                      setCartridgesVisible(false)
                    }}
                  />
                  {cartridgesVisible && (
                    <BuyCartridges
                      cartridges={collection.cartridges}
                      handleAddCartridge={handleAddCartridge}
                      handleCartridgeRemove={handleCartridgeRemove}
                    />
                  )}
                </BuySection>
              )}

              {collection.skins.length > 0 && !collection.hideSkins && (
                <BuySection id="epod-skins" heading={'We thought you might be interested in this too'}>
                  <Addon
                    figure={collection.skins[0].variants[0].image.originalSrc}
                    heading="ePod skins"
                    price="$3.99"
                    description="Add a pattern, a mini icon or a text to your device"
                    onAdd={() => {
                      setSkinsModaVisible(true)
                    }}
                    products={collection.skins.filter(p => p.variants.find(v => v.quantity > 0))}
                    productType="skins"
                    isAvailableForSale
                    handleSkinRemove={handleSkinRemove}
                  />
                  {skinsModalVisible && (
                    <BuySkins setSkinsModaVisible={setSkinsModaVisible} handleAddSkins={handleAddSkins} />
                  )}
                </BuySection>
              )}

              {!collection.hideAccessories && (
                <BuyAccessories
                  accessories={collection.accessories}
                  handleAccessoryQuantityChange={handleAccessoryQuantityChange}
                />
              )}
            </div>
          </>
        )}
      </Chrome>
    </>
  )
}

BuyTemplate.propTypes = {
  pageContext: object.isRequired
}

export default BuyTemplate
